import { Component } from 'react'
import { FormSchemaData, OperationReadingProps, RemoteOperation, Router } from 'stylewhere/shared'
import { EncodingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  Encodings,
  EncodingValidationRequest,
  EncodingValidationResponse,
  TrmEncodingVerifyGroupedTag,
  TrmEncodingVerifyTag
} from 'stylewhere/api'
import { Page, FormSchemaForm, Box, FullLoadingLayer, Button, IdentifierListModal } from 'stylewhere/components'
import { EncodingExtensions } from 'stylewhere/extensions'
import { T, __ } from 'stylewhere/shared/i18n'
import {
  showToastError,
  isModalError,
  canKillToAssociateItem,
  getGroupedKillTags,
  showToast,
  sleep
} from 'stylewhere/shared/utils'

interface State {
  encodingValidation: EncodingValidationRequest
  encodingValidationResponse?: EncodingValidationResponse
  processing: boolean
  data: { uhf_tag: string; nfc_tag: string }
  visibleModalTags: boolean
  targetTag: TrmEncodingVerifyTag[]
  tagGroupSelected?: TrmEncodingVerifyGroupedTag
}

export default class EncodingKillVirtuale extends Component<OperationReadingProps<State>, State> {
  matchParams = Router.getMatchParams(this.props)
  operation = RemoteOperation.getOperationConfig<EncodingOperationConfig>(this.matchParams.opCode)
  isModal = false
  countTags = 0

  state: State = {
    encodingValidation: {
      operationId: this.operation.id,
      identifiers: []
    },
    processing: false,
    data: {
      uhf_tag: '',
      nfc_tag: ''
    },
    visibleModalTags: false,
    targetTag: []
  }

  componentDidMount() {
    this.isModal = isModalError(this.operation)
  }

  onSubmit = async (formSchemaData: FormSchemaData) => {
    this.setState(
      (prevState) => ({
        processing: true,
        encodingValidation: {
          ...prevState.encodingValidation,
          identifiers: [
            {
              identifierType: 'NFC_TAG',
              code: formSchemaData.nfc_tag,
              tid: ''
            },
            {
              identifierType: 'UHF_TAG',
              code: formSchemaData.uhf_tag,
              tid: ''
            }
          ]
        }
      }),
      this.verify
    )
  }

  verify = async () => {
    const { encodingValidation } = this.state
    try {
      const encode = await EncodingExtensions.verify(encodingValidation, true)
      this.setState(
        {
          encodingValidation: encode.encodingValidation,
          encodingValidationResponse: encode.encodingValidationResponse,
          processing: false
        },
        this.canKillTag
      )
    } catch (error) {
      this.setState({ processing: false })
      showToastError(
        (error as any).message ?? 'Unknown error during encoding validation',
        __(T.error.error),
        this.isModal
      )
    }
  }

  getTags = () => {
    const { encodingValidation } = this.state
    const validTagTypes = ['UHF_TAG', 'NFC_TAG', 'NFC_Tag']
    const tagsToKill: TrmEncodingVerifyTag[] = []
    if (encodingValidation && encodingValidation.identifiers) {
      encodingValidation.identifiers.map((idt) => {
        if (validTagTypes.includes(idt.identifierType) && idt.code && idt.code !== '') {
          const read = true
          tagsToKill.push({
            code: idt.code ?? '',
            tid: idt.identifierType === 'UHF_TAG' && idt.tid && idt.tid !== null ? idt.tid : undefined,
            type: idt.identifierType === 'UHF_TAG' ? 'UHF' : 'NFC',
            role: idt.role ?? '',
            associate:
              idt.status === 'ASSOCIATED' && (read || idt._status === 'EXPECTED' || idt._errorCode === 'MISSING_TAG'),
            disassociate: false,
            read: read,
            done: false,
            progress: false,
            group: idt.group ?? '',
            itemId: idt.itemId ?? undefined,
            productionOrderRowId: idt.productionOrderRowId,
            onlyforreplace: false,
            killable: true,
            killed_tag: idt._errorCode === 'KILLED_TAG',
            logicKillable: idt.logicKillable !== undefined ? idt.logicKillable : false,
            tokill:
              read &&
              idt._errorCode !== 'KILLED_TAG' &&
              ((canKillToAssociateItem() && idt.status === 'ASSOCIATED' && idt._status !== 'EXPECTED') ||
                (idt._errorCode !== undefined &&
                  (idt._errorCode.indexOf('Tag Mismatch') !== -1 ||
                    idt._errorCode.indexOf('UNKNOWN_TAG') !== -1 ||
                    idt._errorCode.indexOf('Not Associated Tag') !== -1)))
          })
        }
      })
    }
    return tagsToKill
  }

  checkIdentifier = (data: any[]) => {
    let proceed = false
    const uhfTag: any = data.filter((d) => d.payload.identifierType === 'UHF_TAG')
    const nfcTag: any = data.filter((d) => d.payload.identifierType === 'NFC_TAG')
    if (uhfTag.length === 1 && nfcTag.length === 1) {
      const uhfGroup = uhfTag[0].payload?.group ?? null
      const uhfTipoTag = uhfTag[0].payload?.attributes?.tipoTag ?? null

      const nfcGroup = nfcTag[0].payload?.group ?? null
      const nfcTipoTag = nfcTag[0].payload?.attributes?.tipoTag ?? null
      if (uhfTipoTag === null && uhfTipoTag === nfcTipoTag && uhfGroup !== null && uhfGroup === nfcGroup) {
        proceed = true
      } else if (parseInt(uhfTipoTag, 10) === 1 && parseInt(nfcTipoTag, 10) === 2) {
        proceed = true
      }
    }
    return {
      proceed: proceed,
      message: __(T.error.kill_tag_not_success)
    }
  }

  canKillTag = () => {
    const { encodingValidationResponse, encodingValidation } = this.state
    const tags = this.getTags()
    if (tags.filter((t) => t.tokill).length > 0) {
      const grouped = getGroupedKillTags(tags, false, true)
      if (encodingValidationResponse.errors.length === 0) {
        this.setState({ targetTag: tags, tagGroupSelected: grouped[0], visibleModalTags: true }, this.checkDisassociate)
      } else {
        const data: any[] = []
        encodingValidation.identifiers.forEach((i) => {
          const find = encodingValidationResponse.errors.find(
            (e) => e.ref === i.code && ['Not Associated Tag', 'KILLED_TAG'].includes(e.errorCode)
          )
          if (find) {
            data.push(find)
          }
        })

        const canKill = this.checkIdentifier(data)
        if (canKill.proceed) {
          this.setState(
            { visibleModalTags: true, targetTag: tags, tagGroupSelected: grouped[0] },
            this.checkDisassociate
          )
        } else {
          this.clear(canKill.message)
        }
      }
    } else {
      this.clear(__(T.error.kill_already_killed))
    }
  }

  clear = (message?: string, success?: boolean) => {
    this.countTags = 0
    this.setState({
      data: {
        uhf_tag: '',
        nfc_tag: ''
      },
      encodingValidation: {
        operationId: this.operation.id,
        identifiers: []
      },
      targetTag: [],
      processing: false,
      visibleModalTags: false,
      tagGroupSelected: undefined
    })
    if (success) {
      showToast({ status: 'success', description: message ?? __(T.messages.tag_killed) })
    } else {
      showToastError(message ?? 'Wrong data', __(T.error.error), this.isModal)
    }
  }

  // check if should do disassociate item
  checkDisassociate = async () => {
    const { targetTag } = this.state

    // find if exist item to disassociate
    const find = targetTag.find((idt) => idt.itemId && idt.itemId !== '')

    // check if is possibile disassociate item
    if (find && find.associate && canKillToAssociateItem()) {
      try {
        // sleep
        await sleep(300)

        // call disassociate api
        await Encodings.disassociate({ itemId: find.itemId, operationId: this.operation.id })

        // find tag by code
        const indexTag = targetTag.findIndex((tg) => tg.code === find.code)

        // if tag found change status
        if (indexTag >= 0) targetTag[indexTag].disassociate = true

        // update state and call function to start kill tag process
        this.setState({ targetTag: targetTag }, this.killTag)
      } catch (error) {
        this.clear()
      }
    } else {
      this.killTag()
    }
  }

  /* starter kill tag process */
  killTag = async () => {
    this.countTags = 0
    this.processKillTag()
  }

  processKillTag = async () => {
    const { targetTag } = this.state
    if (this.countTags < targetTag.length) {
      // sleep
      await sleep(300)

      // find tag by code and type
      const indexTag = targetTag.findIndex(
        (tg) => tg.code === targetTag[this.countTags].code && tg.type === targetTag[this.countTags].type
      )

      // if tag found change status
      if (indexTag >= 0) targetTag[indexTag].progress = true

      // update state and call function to kill current tag
      this.setState({ targetTag: targetTag }, this.killingSingleTag)
    } else {
      setTimeout(() => {
        // kill completed
        this.clear(__(T.messages.tag_killed), true)
      }, 1000)
    }
  }

  killingSingleTag = async () => {
    const { targetTag } = this.state
    // kill single tag
    if (targetTag[this.countTags].tokill) {
      try {
        // call be to change tag status to KILLED
        await Encodings.kill(this.operation.id, targetTag[this.countTags] ?? undefined)
      } catch (e) {
        console.log('failure to change tag status to killed')
      }
    }

    // find tag by code and type
    const indexTag = targetTag.findIndex(
      (tg) => tg.code === targetTag[this.countTags].code && tg.type === targetTag[this.countTags].type
    )

    // if tag found change status
    if (indexTag >= 0) {
      targetTag[indexTag].done = true
      targetTag[indexTag].progress = false
    }

    // increment counter for tags to kill
    this.countTags++

    //update state and recall process kill tag to continue flow
    this.setState({ targetTag: targetTag }, this.processKillTag)
  }

  closeIdentifiersModal = () => {
    this.setState({ visibleModalTags: false })
  }

  render() {
    const { processing, data, visibleModalTags, targetTag, tagGroupSelected } = this.state
    return (
      <Page title={this.operation.description} enableEmulation>
        <Box flex center>
          <FormSchemaForm
            style={{ justifyContent: 'space-between' }}
            schema={EncodingExtensions.formSchema(this.operation, {})}
            onSubmit={this.onSubmit}
            submitText={__(T.misc.verify)}
            operation={this.operation}
            formDataUpdated={data}
          />
        </Box>
        {visibleModalTags && (
          <IdentifierListModal
            visible
            tags={targetTag}
            onClose={this.closeIdentifiersModal}
            onConfirm={() => {}}
            progress
            selected={tagGroupSelected}
            replace={false}
            useEmptyGroup
          />
        )}
        {processing && <FullLoadingLayer message={__(T.messages.operation_in_progress)} />}
      </Page>
    )
  }
}
